import axios from 'axios'
import { useState } from 'react'
import posthog from 'posthog-js'
import { trackEvent } from '@/lib/trackEvent'
import { useDispatchData } from './useDispatchData'
import { endpoints } from '@/services/api/endpoints'
import { useGoogleLogin } from '@react-oauth/google'

interface UserPayload {
  user: {
    account_type: 'google' | 'magic_link'
    account_id?: string
    name?: string
    email: string
    profile_image?: string
    password?: string
  }
}

export const useLoginUser = () => {
  const [loginError, setLoginError] = useState()
  const [magicLinkSent, setMagicLinkSent] = useState(false)
  const { dispatchData } = useDispatchData({
    trackLocation: true,
    useIPLocation: true,
  })

  const handleMagicLinkLogin = async (email: string) => {
    trackEvent('MagicLink Login Attempted', { email })

    try {
      const response = await axios.post(endpoints.login.url, {
        user: { account_type: 'magic_link', email },
      })
      if (response.status === 200) {
        setMagicLinkSent(true)
      }
    } catch (error) {
      console.error(error)
      setLoginError(error)
      trackEvent('MagicLink Login Failed', {
        error: error.response.data,
      })
    }
  }

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        trackEvent('Google Login Attempted')

        const userInfoResponse = await fetch(
          'https://www.googleapis.com/oauth2/v3/userinfo',
          {
            headers: {
              Authorization: `Bearer ${tokenResponse.access_token}`,
            },
          }
        )
        const userInfo = await userInfoResponse.json()

        const userPayload: UserPayload = {
          user: {
            account_type: 'google',
            account_id: userInfo.sub,
            name: userInfo.name,
            email: userInfo.email,
            profile_image: userInfo.picture,
          },
        }

        const response = await axios.post(endpoints.login.url, userPayload)
        const userData = response.data.data
        const userDetails = {
          name: userInfo.name,
          email: userInfo.email,
          picture: userInfo.picture,
          user_id: userData.user_id,
          last_login: userData.last_login,
          role: userData.role,
          token: userData.access_token,
          is_onboarded: userData.is_onboarded,
        }
        dispatchData(userDetails)

        trackEvent('Google Login Successful', {
          accountType: 'google',
          userEmail: userInfo.email,
        })

        posthog.identify(userData.user_id, {
          $set: {
            name: userInfo.name,
            email: userInfo.email,
          },
        })

        posthog.capture('user_logged_in', {
          login_type: 'google',
          user_email: userInfo.email,
          user_id: userData.user_id,
        })
      } catch (error) {
        console.error('Error fetching user info:', error)
        trackEvent('Google Login Failed', { error: error.response.data })
        setLoginError(error)
      }
    },
    onError: (error) => {
      console.error('Google login failed:', error)
      trackEvent('Google Login Failed', { error: error.response.data })
      setLoginError(error)
    },
    flow: 'implicit',
  })

  return {
    magicLinkSent,
    loginError,
    handleMagicLinkLogin,
    handleGoogleLogin,
  }
}
